import { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Spinner from "aldoo-ra/Spinner"
import { GetQuota } from "@components/FileShareFeature/api"

const GB = 1024 ** 3
const TB = 1024 ** 4
function fmtSize(bytes) {
  if (bytes == null) return "∞"
  if (bytes >= TB) return `${(bytes / TB).toFixed(bytes % TB === 0 ? 0 : 1)} TB`
  if (bytes >= GB) return `${(bytes / GB).toFixed(bytes % GB === 0 ? 0 : 1)} GB`
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(0)} MB`
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${bytes} B`
}

/// Countdown formatter for the rolling-30d reset moment. Returns null when
/// there's nothing to count down (no resetsAt, or it's already in the
/// past — in practice the server omits resetsAt when nothing is counted).
function formatCountdown(resetsAt) {
  if (!resetsAt) return null
  const t = new Date(resetsAt).getTime()
  if (!Number.isFinite(t)) return null
  const diff = t - Date.now()
  if (diff <= 0) return null
  const days = Math.floor(diff / 86400000)
  const hours = Math.floor((diff % 86400000) / 3600000)
  const mins = Math.floor((diff % 3600000) / 60000)
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${mins}m`
  return `${mins}m`
}

// One usage stat with a progress bar (or a plain value when there's no
// "used" notion). `resetsAt` drives a "Resets in Nd Nh" countdown under
// limited cards so the user sees when the rolling window frees up.
function StatCard({ label, used, limit, format, unlimitedLabel, resetsAt, resetLabel }) {
  const unlimited = limit == null
  const pct = unlimited || !limit ? 0 : Math.min(100, Math.round((used / limit) * 100))
  const barColor = pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-primary"

  // Live tick so the countdown updates every minute without a refetch.
  const [, force] = useState(0)
  useEffect(() => {
    if (unlimited || !resetsAt) return
    const id = setInterval(() => force((n) => n + 1), 60_000)
    return () => clearInterval(id)
  }, [unlimited, resetsAt])

  const countdown = unlimited ? null : formatCountdown(resetsAt)

  return (
    <div className="bg-paper rounded-2xl border border-bordered p-5">
      <div className="flex items-baseline justify-between mb-2">
        <Typography variant="label_upper_dim">{label}</Typography>
        <Typography variant="txt2_accent" className="text-text">
          {unlimited
            ? <span className="text-primary">{unlimitedLabel}</span>
            : <>{format(used)} <span className="text-text_secondary">/ {format(limit)}</span></>}
        </Typography>
      </div>
      <div className="h-2 rounded-full bg-neutral_1 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${unlimited ? "bg-primary/30" : barColor}`}
          style={{ width: unlimited ? "100%" : `${pct}%` }}
        />
      </div>
      <div className="mt-2 text-xs text-text_secondary">
        {unlimited
          ? unlimitedLabel
          : countdown
            ? `${resetLabel} ${countdown}`
            : (resetLabel ? `${resetLabel} —` : "")}
      </div>
    </div>
  )
}

// A plan limit shown as a value (no rolling usage), e.g. max size / max duration.
function ValueCard({ label, value }) {
  return (
    <div className="bg-paper rounded-2xl border border-bordered p-5">
      <Typography variant="label_upper_dim" className="block mb-1">{label}</Typography>
      <Typography variant="h4" className="text-text my-0">{value}</Typography>
    </div>
  )
}

export default function UsagePanel({ texts }) {
  const [quota, setQuota] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    GetQuota()
      .then((q) => { if (q?.result) setQuota(q) })
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-12 text-text_secondary">
        <Spinner className="w-[18px] h-[18px]" />
        {texts?.loading}
      </div>
    )
  }
  if (!quota) {
    return <Typography variant="txt_dim">{texts?.usageUnavailable}</Typography>
  }

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="txt2_dim" className="block text-center">
        {texts?.rolling30Window}
      </Typography>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard
          label={texts?.volume30}
          used={quota.volumeUsed}
          limit={quota.volumeLimit}
          format={fmtSize}
          unlimitedLabel={texts?.unlimited}
          resetsAt={quota.resetsAt}
          resetLabel={texts?.resetsIn}
        />
        <StatCard
          label={texts?.transfers30}
          used={quota.countUsed}
          limit={quota.countLimit}
          format={(n) => String(n)}
          unlimitedLabel={texts?.unlimited}
          resetsAt={quota.resetsAt}
          resetLabel={texts?.resetsIn}
        />
        <ValueCard label={texts?.maxSizePerTransfer} value={fmtSize(quota.maxSize)} />
        <ValueCard label={texts?.maxLinkDuration} value={`${quota.maxRetentionDays} ${texts?.days}`} />
      </div>
    </div>
  )
}
